<template>
    <div class="col-sm-12">
        <p class="nopads mt-6 mb-4">
            {{ $t("sensor_device.devices_and_sensors") }}
        </p>

        <div class="col-12 list-container nopads">
            <div
                v-for="device in resultList"
                :key="device.id"
                class="mb-5 border p-4 bg-light-subtle rounded"
            >
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">
                        {{ device.name ? device.name : '-' }}

                        <span
                            class="ms-2 fw-normal"
                            style="font-size: 0.85em"
                        >
                            ID: {{ device.device_id ? device.device_id : '-' }}
                        </span>
                    </h6>

                    <div>
                        <div
                            class="badge me-2 py-2"
                            :class="{ 'text-bg-secondary': !device.is_active, 'text-bg-primary': device.is_active }"
                        >
                            {{ device.is_active ? $t('sensor_device.is_active') : $t('sensor_device.is_not_active') }}
                        </div>

                        <button
                            class="btn btn-sm btn-secondary"
                            @click="$emit('editDevice', device)"
                        >
                            <i class="fa fa-pen" />
                        </button>
                    </div>
                </div>
                <table
                    v-if="device.sensors && device.sensors.length > 0"
                    class="table table-sm table-borderless mt-2"
                    style="--bs-table-bg: transparent"
                >
                    <thead>
                        <tr>
                            <th>{{ $t('sensor_device.name') }}</th>
                            <th>{{ $t('sensor_device.display_name') }}</th>
                            <th>{{ $t('sensor_device.report_title') }}</th>
                            <th>{{ $t('sensor_device.show_in_app') }}</th>
                            <th>{{ $t('sensor_device.unit') }}</th>
                            <th>{{ $t('sensor_device.calibration_multiplier') }}</th>
                            <th>{{ $t('sensor_device.offset') }}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="sensor in device.sensors"
                            :key="sensor.id"
                        >
                            <td>
                                <input
                                    v-model="sensor.name"
                                    class="form-control form-control-sm"
                                    :class="{ 'is-invalid': invalidSensorIds.includes(sensor.id) && !sensor.name?.trim() }"
                                >
                            </td>
                            <td>
                                <input
                                    v-model="sensor.display_name"
                                    class="form-control form-control-sm"
                                    :class="{ 'is-invalid': invalidSensorIds.includes(sensor.id) && !sensor.display_name?.trim() }"
                                >
                            </td>
                            <td>
                                <select
                                    v-model="sensor.report_name"
                                    class="form-select form-select-sm"
                                >
                                    <option value="">
                                        {{ $t('common.select') }}
                                    </option>
                                    <option value="Päivämäärä">
                                        Päivämäärä
                                    </option>
                                    <option value="Aikaleima">
                                        Aikaleima
                                    </option>
                                    <option value="Latitude">
                                        Latitude
                                    </option>
                                    <option value="Longitude">
                                        Longitude
                                    </option>
                                    <option value="Altitude">
                                        Altitude
                                    </option>
                                    <option value="Jyrsinsyv_oik">
                                        Jyrsinsyv_oik
                                    </option>
                                    <option value="Jyrsinsyv_vas">
                                        Jyrsinsyv_vas
                                    </option>
                                    <option value="L_al">
                                        L_al
                                    </option>
                                    <option value="L_oik">
                                        L_oik
                                    </option>
                                    <option value="L_kes">
                                        L_kes
                                    </option>
                                    <option value="L_vas">
                                        L_vas
                                    </option>
                                    <option value="Leveys">
                                        Leveys
                                    </option>
                                </select>
                            </td>
                            <td class="align-middle">
                                <span
                                    :title="!sensor.display_name?.trim() ? $t('sensor_device.display_name_required_for_app') : ''"
                                >
                                    <input
                                        v-model="sensor.show_in_app"
                                        type="checkbox"
                                        class="form-check-input"
                                        :disabled="!sensor.display_name?.trim()"
                                    >
                                </span>
                            </td>
                            <td>
                                <input
                                    v-model="sensor.unit"
                                    class="form-control form-control-sm"
                                >
                            </td>
                            <td>
                                <input
                                    v-model="sensor.calibration_multiplier"
                                    type="number"
                                    step="0.000001"
                                    class="form-control form-control-sm"
                                >
                            </td>
                            <td>
                                <input
                                    v-model="sensor.offset"
                                    type="number"
                                    class="form-control form-control-sm"
                                >
                            </td>
                            <td class="text-nowrap">
                                <button
                                    class="btn btn-sm btn-outline-danger"
                                    @click="removeSensor(device.id, sensor)"
                                >
                                    <i class="fa fa-trash" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="d-flex justify-content-between mt-1">
                    <button
                        class="btn btn-sm btn-primary"
                        @click="addSensorRow(device)"
                    >
                        + {{ $t("sensor_device.sensor") }}
                    </button>

                    <div class="d-flex align-items-center">
                        <span
                            v-if="dirtyDevices.has(device.id) && !(invalidSensorIdsByDevice[device.id] || []).length"
                            class="text-danger me-2"
                        >
                            {{ $t('sensor_device.unsaved_changes') }}
                        </span>
                        <button
                            class="btn btn-md btn-success"
                            :disabled="(invalidSensorIdsByDevice[device.id] || []).length > 0"
                            @click="saveAll(device)"
                        >
                            {{ $t("common.save") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { vehicleHelper } from "@/components/mixins/VehicleMixin";

export default {
    name: 'SensorDeviceList',
    components: {},
    mixins: [vehicleHelper],
    props: {
        results: {
            type: Array,
            default: function () {
                return []
            },
        }
    },
    data() {
        return {
            originalSensors: {},
            sensorsModified: false
        }
    },
    computed: {
        resultList() {
            return this.results || []
        },
        dirtyDevices() {
            return new Set(
                this.resultList
                    .filter(device => (device.sensors || []).some(sensor => this.isDirty(sensor)))
                    .map(device => device.id)
            );
        },
        invalidSensorIds() {
            return this.resultList
                .flatMap(device => device.sensors || [])
                .filter(s => !s.name?.trim() || !s.display_name?.trim())
                .map(s => s.id);
        },
        invalidSensorIdsByDevice() {
            const map = {};
            this.resultList.forEach(device => {
                map[device.id] = (device.sensors || [])
                    .filter(s => !s.name?.trim() || !s.display_name?.trim())
                    .map(s => s.id);
            });
            return map;
        }
    },
    watch: {
        results: {
            immediate: true,
            handler(val) {
                (val || []).forEach(device => {
                    (device.sensors || []).forEach(sensor => {
                        if (!this.originalSensors[sensor.id]) {
                            this.originalSensors[sensor.id] = { ...sensor };
                        }
                    });
                });
            }
        }
    },
    methods: {
        updateOriginalSensors(sensors) {
            sensors.forEach(sensor => {
                this.originalSensors[sensor.id] = { ...sensor };
            });
        },
        addSensorRow(device) {
            const newSensor = {
                id: 'new_' + Date.now(),
                name: '',
                display_name: '',
                report_name: '',
                show_in_app: false,
                unit: '',
                calibration_multiplier: 1,
                offset: 0
            };

            device.sensors = [...(device.sensors || []), newSensor];
        },
        // check if each row is modified from its original state.
        areSensorsDirty(sensors) {
            return sensors.some(sensor => this.isDirty(sensor));
        },
        isDirty(sensor) {
            if (String(sensor.id).startsWith('new_')) return true;
            const orig = this.originalSensors[sensor.id];
            if (!orig) return false;
            return sensor.name !== orig.name || sensor.unit !== orig.unit || sensor.calibration_multiplier !== orig.calibration_multiplier || sensor.display_name !== orig.display_name || sensor.show_in_app !== orig.show_in_app || sensor.report_name !== orig.report_name || sensor.offset !== orig.offset;
        },
        saveSensor(deviceId, sensor) {
            this.$emit('updateSensor', deviceId, sensor);
        },
        saveAll(device) {
            const sensors = device.sensors || [];
            if ((this.invalidSensorIdsByDevice[device.id] || []).length) {
                this.$emit('validationError', this.$t('sensor_device.sensor_required_fields'));
                return;
            }
            this.$emit('updateSensors', device.id, sensors);
        },
        removeSensor(deviceId, sensor) {
            if (String(sensor.id).startsWith('new_')) {
                const device = this.resultList.find(d => d.id === deviceId);
                if (device) device.sensors = device.sensors.filter(s => s.id !== sensor.id);
            } else {
                this.$emit('removeSensor', deviceId, sensor);
            }
        },
        removeDevice(device) {
            this.$emit('removeDevice', device.id)
        },
    }
}
</script>

<style scoped>
.result-table :deep(.routa-table-cell) input.form-control {
    border: 1px solid #ced4da !important;
}

table td,
table th {
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
}

.form-control.is-invalid,
.form-select.is-invalid {
    padding-right: 0rem;
}
</style>
